"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import styles from "./ai-features.module.scss";

const CoachHead3D = dynamic(() => import("./CoachHead3D"), { ssr: false });

const EYE_CONTOURS = {
  "fox-1": [2, 3],
  "fox-2": [1, 2],
  "fox-4": [2, 3],
  "fox-5": [2, 3],
};
type FoxVariant = keyof typeof EYE_CONTOURS;
type CoachArtwork = { variant: FoxVariant; markup: string; source: string };
const EAR_SPRING = (t: number) => 1 - Math.exp(-6.5 * t) * Math.cos(11 * t);
const OUT_EXPO = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
const OUT_CUBIC = (t: number) => 1 - Math.pow(1 - t, 3);
const IN_OUT_CUBIC = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const IDLE_LOOK_TIMING = {
  duration: 2.8,
  times: [0, 0.15, 0.4, 0.58, 0.83, 1],
  ease: [
    OUT_CUBIC,
    (t: number) => t,
    IN_OUT_CUBIC,
    (t: number) => t,
    IN_OUT_CUBIC,
  ],
};
const IDLE = {
  scale: [1, 1.012, 1, 0.988, 1],
  y: [0, -0.5, 0],
  rotateZ: [0, -0.15, 0, 0.15, 0],
};
const STILL = { scale: 1, y: 0, rotateZ: 0 };
const earPaths = new WeakMap<
  SVGPathElement,
  { command: string; x: number; y: number }[]
>();

function earPoints(path: SVGPathElement) {
  const cached = earPaths.get(path);
  if (cached) return cached;
  const tokens =
    (path.getAttribute("d") || "").match(
      /[MLHVZ]|[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/g,
    ) || [];
  const points: { command: string; x: number; y: number }[] = [];
  let x = 0,
    y = 0,
    command = "M";
  for (let i = 0; i < tokens.length;) {
    if (/^[MLHVZ]$/.test(tokens[i])) command = tokens[i++];
    if (command === "Z") {
      points.push({ command, x, y });
      continue;
    }
    if (command === "H") x = Number(tokens[i++]);
    else if (command === "V") y = Number(tokens[i++]);
    else {
      x = Number(tokens[i++]);
      y = Number(tokens[i++]);
    }
    points.push({ command: command === "M" ? "M" : "L", x, y });
    if (command === "M") command = "L";
  }
  earPaths.set(path, points);
  return points;
}
const smooth = (min: number, max: number, value: number) => {
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return t * t * (3 - 2 * t);
};

function movableOriginalEyes(source: string, variant: FoxVariant, id: string) {
  // Only the four checked-in SVGs are loaded here, never CMS or user markup.
  const svg = new DOMParser().parseFromString(
    source,
    "image/svg+xml",
  ).documentElement;
  const face = svg.querySelector("path");
  const contours = face?.getAttribute("d")?.match(/M[^M]*/g);
  if (!face || !contours) throw new Error("Coach SVG has no face contours");
  const eyes = EYE_CONTOURS[variant];
  const ns = "http://www.w3.org/2000/svg";
  const mask = document.createElementNS(ns, "mask");
  mask.id = id;
  mask.setAttribute("maskUnits", "userSpaceOnUse");
  mask.style.maskType = "luminance";
  const background = document.createElementNS(ns, "rect");
  background.setAttribute("width", "100%");
  background.setAttribute("height", "100%");
  background.setAttribute("fill", "white");
  const holes = document.createElementNS(ns, "g");
  holes.setAttribute("data-original-eyes", "");
  for (const index of eyes) {
    const hole = document.createElementNS(ns, "path");
    hole.setAttribute("d", contours[index]);
    hole.setAttribute("fill", "black");
    holes.append(hole);
  }
  mask.append(background, holes);
  const defs =
    svg.querySelector("defs") || document.createElementNS(ns, "defs");
  defs.append(mask);
  if (!defs.parentNode) svg.append(defs);
  // Preserve the original transparent eye cutouts instead of painting new eyes.
  face.setAttribute(
    "d",
    contours.filter((_, index) => !eyes.includes(index)).join(""),
  );
  face.setAttribute("mask", "url(#" + id + ")");
  face.setAttribute("fill", "white");
  svg.setAttribute("aria-hidden", "true");
  return new XMLSerializer().serializeToString(svg);
}

export default function InteractiveCoach({
  variant,
  reaction,
}: {
  variant: FoxVariant;
  reaction: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const drawing = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const [rendererReady, setThreeReady] = useState(false);
  const [shownVariant, setShownVariant] = useState(variant);
  const displayed = useRef(variant);
  const cache = useRef(new Map<FoxVariant, CoachArtwork>());
  const [artwork, setArtwork] = useState<CoachArtwork>();
  const threeReady = rendererReady && artwork?.variant === shownVariant;
  const eyeOpen = useMotionValue(1);
  const earTilt = useMotionValue(0);
  const eyeAnimation = useRef<{ stop: () => void } | null>(null);
  const previousReaction = useRef(reaction);
  const awake = inView && visible && !reducedMotion;
  const id = "coach-eyes-" + useId().replaceAll(":", "");
  const targetX = useMotionValue(0);
  const targetY = useMotionValue(0);
  const idleTilt = useMotionValue(0);
  const roll = useSpring(idleTilt, { stiffness: 90, damping: 22, mass: 0.7 });
  const gazeX = useSpring(targetX, { stiffness: 220, damping: 28, mass: 0.4 });
  const gazeY = useSpring(targetY, { stiffness: 220, damping: 28, mass: 0.4 });
  const headX = useSpring(targetX, { stiffness: 90, damping: 22, mass: 0.7 });
  const headY = useSpring(targetY, { stiffness: 90, damping: 22, mass: 0.7 });
  const rotateX = useTransform(headY, [-1, 1], [10, -10]);
  const rotateY = useTransform(headX, [-1, 1], [-14, 14]);

  const updateEyes = useCallback(() => {
    if (threeReady) return;
    const svg = drawing.current?.querySelector("svg");
    const eyes = svg?.querySelector("[data-original-eyes]");
    if (!svg || !eyes) return;
    const openness = eyeOpen.get();
    for (const path of eyes.querySelectorAll<SVGPathElement>("path")) {
      path.style.transformBox = "fill-box";
      path.style.transformOrigin = "center";
      path.style.transform = `scaleY(${openness})`;
    }
    const size = svg.viewBox.baseVal.width;
    const x = reducedMotion ? 0 : gazeX.get() * size * 0.004;
    const y = reducedMotion ? 0 : gazeY.get() * size * 0.0025;
    eyes.setAttribute("transform", "translate(" + x + " " + y + ")");
  }, [gazeX, gazeY, reducedMotion, eyeOpen, threeReady]);
  useMotionValueEvent(gazeX, "change", updateEyes);
  useMotionValueEvent(gazeY, "change", updateEyes);
  useMotionValueEvent(eyeOpen, "change", updateEyes);
  const updateEars = useCallback(() => {
    if (threeReady) return;
    const tilt = earTilt.get();
    const svg = drawing.current?.querySelector("svg");
    const path = svg?.querySelector<SVGPathElement>(":scope > path");
    if (!svg || !path) return;
    const { x: left, y: top, width, height } = svg.viewBox.baseVal;
    path.setAttribute(
      "d",
      earPoints(path)
        .map((point) => {
          if (point.command === "Z") return "Z";
          const x = point.x - left - width / 2;
          const y = top + height / 2 - point.y;
          const side = Math.sign(x);
          const weight =
            smooth(width * 0.04, width * 0.08, Math.abs(x)) *
            smooth(height * 0.03, height * 0.05, y);
          const angle = ((side * tilt * Math.PI) / 180) * weight;
          const pivotX = left + width / 2 + side * width * 0.2;
          const pivotY = top + height / 2 - height * 0.08;
          const dx = point.x - pivotX,
            dy = point.y - pivotY;
          return `${point.command}${pivotX + Math.cos(angle) * dx - Math.sin(angle) * dy} ${pivotY + Math.sin(angle) * dx + Math.cos(angle) * dy}`;
        })
        .join(""),
    );
  }, [earTilt, threeReady]);
  useMotionValueEvent(earTilt, "change", updateEars);
  useLayoutEffect(updateEars, [artwork, updateEars]);
  useLayoutEffect(updateEyes, [artwork, updateEyes]);

  const commitShape = useCallback((next: FoxVariant) => {
    if (displayed.current === next) return;
    displayed.current = next;
    setThreeReady(false);
    setShownVariant(next);
    setArtwork((current) => cache.current.get(next) ?? current);
  }, []);
  const blink = useCallback(
    (reacting = false) => {
      eyeAnimation.current?.stop();
      eyeAnimation.current = animate(
        eyeOpen,
        reacting ? [eyeOpen.get(), 0.55, 1] : [eyeOpen.get(), 0.1, 0.1, 1],
        reacting
          ? {
              duration: 0.42,
              times: [0, 2 / 7, 1],
              ease: [OUT_CUBIC, OUT_EXPO],
            }
          : {
              duration: 0.24,
              times: [0, 7 / 24, 11 / 24, 1],
              // A brief closed hold keeps the blink visible at lower frame rates.
              ease: [(t) => t * t * t, "linear", OUT_EXPO],
            },
      );
    },
    [eyeOpen],
  );

  useEffect(() => {
    const changed =
      previousReaction.current !== reaction || displayed.current !== variant;
    previousReaction.current = reaction;
    if (!awake) {
      earTilt.set(0);
      commitShape(variant);
      return;
    }
    if (!changed) return;
    blink(true);
    let ears = animate(earTilt, -9, {
      duration: 0.22,
      ease: OUT_EXPO,
    });
    const apex = window.setTimeout(() => {
      commitShape(variant);
      ears.stop();
      ears = animate(earTilt, 6, { duration: 0.3, ease: EAR_SPRING });
    }, 180);
    const settle = window.setTimeout(() => {
      ears.stop();
      ears = animate(earTilt, 0, { duration: 0.3, ease: EAR_SPRING });
    }, 480);
    return () => {
      window.clearTimeout(apex);
      window.clearTimeout(settle);
      ears.stop();
    };
  }, [variant, reaction, awake, blink, commitShape, earTilt]);

  useEffect(() => {
    if (!awake) {
      eyeAnimation.current?.stop();
      eyeOpen.set(1);
      return;
    }
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(
        () => {
          blink();
          schedule();
        },
        2600 + Math.random() * 2600,
      );
    };
    schedule();
    return () => {
      window.clearTimeout(timer);
      eyeAnimation.current?.stop();
    };
  }, [awake, reaction, blink, eyeOpen]);

  useEffect(() => {
    const controller = new AbortController();
    const prepared = cache.current.get(variant);
    if (prepared) {
      if (displayed.current === variant) setArtwork(prepared);
      return;
    }
    fetch("/ai-features/coach-faces/" + variant + ".svg", {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Coach SVG could not load");
        return response.text();
      })
      .then((source) => {
        if (controller.signal.aborted) return;
        const prepared = {
          variant,
          markup: movableOriginalEyes(source, variant, id),
          source,
        };
        cache.current.set(variant, prepared);
        if (displayed.current === variant) setArtwork(prepared);
      })
      .catch(() => {
        // The original image remains visible if the inline artwork cannot load.
      });
    return () => controller.abort();
  }, [variant, id]);

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === "visible");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    let pointer: { x: number; y: number } | null = null;
    let timer: number;
    let idlePlaying = false;
    let idleAnimations: { stop: () => void }[] = [];
    const stopIdle = () => {
      window.clearTimeout(timer);
      idleAnimations.forEach((animation) => animation.stop());
      idleAnimations = [];
      idlePlaying = false;
      idleTilt.set(0);
    };
    const reset = () => {
      stopIdle();
      pointer = null;
      targetX.set(0);
      targetY.set(0);
    };
    if (!awake) {
      reset();
      return;
    }
    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(idleLook, delay);
    };
    function idleLook() {
      pointer = null;
      idlePlaying = true;
      idleAnimations = [
        animate(
          targetX,
          [targetX.get(), -0.5, -0.5, 0.35, 0.35, 0],
          IDLE_LOOK_TIMING,
        ),
        animate(
          targetY,
          [targetY.get(), 0, 0, 0.28, 0.28, 0],
          IDLE_LOOK_TIMING,
        ),
        animate(idleTilt, [idleTilt.get(), -2.5, -2.5, 1.5, 1.5, 0], {
          ...IDLE_LOOK_TIMING,
          onComplete: () => {
            idlePlaying = false;
            schedule(1700 + Math.random() * 1600);
          },
        }),
      ];
    }
    const update = () => {
      if (idlePlaying) return;
      const bounds = ref.current?.parentElement?.getBoundingClientRect();
      if (!bounds) return;
      const depth = Math.max(bounds.width * 1.5, 140);
      const x = pointer ? pointer.x - bounds.left - bounds.width * 0.5 : 0;
      const y = pointer ? pointer.y - bounds.top - bounds.height * 0.55 : 0;
      targetX.set(x / Math.hypot(x, depth));
      targetY.set(y / Math.hypot(y, depth));
    };
    const follow = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      stopIdle();
      pointer = { x: event.clientX, y: event.clientY };
      update();
      schedule(2600);
    };
    const resume = () => schedule(1200);
    resume();
    window.addEventListener("pointermove", follow, { passive: true });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    window.addEventListener("blur", reset);
    window.addEventListener("focus", resume);
    return () => {
      window.removeEventListener("pointermove", follow);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("blur", reset);
      window.removeEventListener("focus", resume);
      reset();
    };
  }, [awake, targetX, targetY, idleTilt]);

  return (
    <motion.span
      ref={ref}
      className={styles.foxFace}
      style={{ rotateZ: awake ? roll : 0 }}
      data-coach-gaze
      data-variant={shownVariant}
      data-awake={awake}
      data-coach-render={threeReady ? "3d" : "svg"}
    >
      <motion.span
        className={styles.foxFallback}
        style={{
          rotateX: reducedMotion || threeReady ? 0 : rotateX,
          rotateY: reducedMotion || threeReady ? 0 : rotateY,
          opacity: threeReady ? 0 : 1,
        }}
      >
        <motion.span
          className={styles.foxBreathing}
          animate={awake && !threeReady ? IDLE : STILL}
          transition={
            awake && !threeReady
              ? { duration: 2, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }
          }
        >
          {artwork?.variant === shownVariant ? (
            <span
              ref={drawing}
              className={styles.foxInline}
              dangerouslySetInnerHTML={{ __html: artwork.markup }}
            />
          ) : (
            <Image
              src={"/ai-features/coach-faces/" + shownVariant + ".svg"}
              width={210}
              height={210}
              alt=""
              className={styles.foxDrawing}
            />
          )}
        </motion.span>
      </motion.span>
      {inView && artwork && (
        <CoachHead3D
          source={artwork.source}
          eyeContours={EYE_CONTOURS[artwork.variant]}
          headX={headX}
          headY={headY}
          gazeX={gazeX}
          gazeY={gazeY}
          earTilt={earTilt}
          eyeOpen={eyeOpen}
          active={awake && artwork.variant === shownVariant}
          shown={artwork.variant === shownVariant}
          onReady={setThreeReady}
        />
      )}
    </motion.span>
  );
}
